import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Which sign-in an email belongs to: home-realm discovery for the one-box /login (Joel,
 * 2026-10-07; design docs/DESIGN-login-home-realm-discovery.md rev 3, contract frozen with
 * DataServices).
 *
 *   entra    : a Vortex staff domain or a customer with its own Entra tenant
 *   external : everything else (External ID at login.vericure.io): unknown, consumer, invalid
 *   google   : a Google Workspace customer domain (reserved; emitted only once Google is enabled)
 */
export type SignInRoute = 'entra' | 'google' | 'external';

/** Why a route could not be decided. The login page shows a clear error with Retry for each. */
export type DiscoverFailure = 'unavailable' | 'rate-limited';

export class DiscoverError extends Error {
  constructor(readonly kind: DiscoverFailure) {
    super(`home-realm discovery ${kind}`);
  }
}

/** The client-side ceiling; the server answers from one Cosmos point read. */
export const DISCOVER_TIMEOUT_MS = 5000;

/** The part after the LAST '@', trimmed and lowercased; '' when the input is not an email. */
export function domainOf(email: string): string {
  const s = (email ?? '').trim();
  const at = s.lastIndexOf('@');
  if (at <= 0 || at === s.length - 1) return '';
  const domain = s.slice(at + 1).toLowerCase();
  return /^[a-z0-9.-]+\.[a-z0-9-]{2,}$/.test(domain) && !domain.includes('..') ? domain : '';
}

/** A plausible email: something before the @, a domain after it. The server owns the real rules. */
export function isPlausibleEmail(email: string): boolean {
  const s = (email ?? '').trim();
  return s.indexOf('@') > 0 && !/\s/.test(s) && domainOf(s) !== '';
}

@Injectable({ providedIn: 'root' })
export class HomeRealmService {
  constructor(private http: HttpClient) {}

  /** The edge URL (Front Door) for auth/discover. Absent = discovery is not configured here. */
  private get url(): string {
    return (environment as { authDiscoverUrl?: string }).authDiscoverUrl ?? '';
  }

  /** Discovery is configured for this environment (beta-dev once the edge is live; never prod2 today). */
  get configured(): boolean {
    return !!this.url;
  }

  /**
   * Ask the server which sign-in this email's DOMAIN uses. Only the domain is sent, never the
   * address: an anonymous, enumerable endpoint gets the minimum.
   *
   * NOT CONFIGURED -> 'entra', with no request: an environment without discovery (prod2 today) has
   * only the staff sign-in, so the box routes everyone there with their email as the hint.
   *
   * FAILURES ARE NEVER A ROUTE. A timeout, network error or 5xx is 'unavailable' and a 429 is
   * 'rate-limited'; the page says so and offers Retry. A default route would send staff and
   * customers with their own Entra tenant to a sign-in page where they have no account.
   */
  discover(email: string): Observable<SignInRoute> {
    if (!this.configured) return of('entra');
    const domain = domainOf(email);
    return this.http.post<{ route?: unknown }>(this.url, { domain }).pipe(
      timeout(DISCOVER_TIMEOUT_MS),
      map((r) => {
        const route = r?.route;
        if (route === 'entra' || route === 'google' || route === 'external') return route;
        // A 200 with no recognisable route is a broken answer, not a routing decision.
        throw new DiscoverError('unavailable');
      }),
      catchError((err: unknown) => {
        if (err instanceof DiscoverError) return throwError(() => err);
        if (err instanceof HttpErrorResponse && err.status === 429) {
          return throwError(() => new DiscoverError('rate-limited'));
        }
        // Timeout, network error, 5xx, anything else: unavailable. Never a route.
        return throwError(() => new DiscoverError('unavailable'));
      })
    );
  }
}

/** localStorage key for the opt-in "Remember my email on this device". */
export const REMEMBERED_EMAIL_KEY = 'vericureRememberedEmail';

/** The remembered email, or '' (none, unreadable storage, or a value that is not an email). */
export function readRememberedEmail(): string {
  try {
    const v = localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '';
    if (v && !isPlausibleEmail(v)) {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      return '';
    }
    return v;
  } catch {
    return '';
  }
}

/** Store it only when the user opted in; forget it otherwise. Storage failure is tolerated. */
export function rememberEmail(email: string, optedIn: boolean): void {
  try {
    if (optedIn && isPlausibleEmail(email)) localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
    else localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    /* private window or blocked storage: simply not remembered */
  }
}

export function forgetRememberedEmail(): void {
  try {
    localStorage.removeItem(REMEMBERED_EMAIL_KEY);
  } catch {
    /* nothing to forget */
  }
}
